import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { apiGet, apiPut } from '../lib/auth'

export interface Site {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  zip?: string
  latitude?: number
  longitude?: number
  timezone?: string
  is_default: boolean
  created_at?: string
}

interface SiteState {
  sites: Site[]
  activeSiteId: string | null
  loaded: boolean
  loading: boolean

  loadSites: () => Promise<void>
  setActiveSite: (siteId: string) => Promise<void>
  activeSite: () => Site | null
  reset: () => void
}

export const useSiteStore = create<SiteState>()(
  persist(
    (set, get) => ({
      sites: [],
      activeSiteId: null,
      loaded: false,
      loading: false,

      loadSites: async () => {
        if (get().loading) return
        set({ loading: true })
        try {
          const [sitesRes, activeRes] = await Promise.all([
            apiGet<{ sites: Site[] }>('/api/v1/sites'),
            apiGet<{ active_site_id: string | null }>('/api/v1/sites/active'),
          ])
          const sites = sitesRes.sites ?? []
          const activeSiteId = activeRes.active_site_id ?? sites[0]?.id ?? null
          set({ sites, activeSiteId, loaded: true, loading: false })
        } catch {
          set({ loading: false, loaded: true })
        }
      },

      setActiveSite: async (siteId: string) => {
        set({ activeSiteId: siteId })
        try {
          await apiPut('/api/v1/sites/active', { site_id: siteId })
        } catch {
          // best-effort persist
        }
      },

      activeSite: () => {
        const { sites, activeSiteId } = get()
        return sites.find((s) => s.id === activeSiteId) ?? sites[0] ?? null
      },

      reset: () => set({ sites: [], activeSiteId: null, loaded: false, loading: false }),
    }),
    {
      name: 'esi-site-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ activeSiteId: state.activeSiteId }),
    },
  ),
)
